import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { BackIcon, CheckIcon, DeviceIcon } from "../components/Icons";
import { LoadingCards, Message } from "../components/Status";
import ui from "../components/ui.module.css";
import { OFFLINE, problemOf } from "../data/problems";
import {
  createSetupCode,
  isTabletSetupAvailable,
  readClassrooms,
} from "../data/tabletSetup";

// Set up a learner tablet: choose the class and the learner, then show a
// one-time code for the tablet to scan. The tablet never gets a teacher's
// sign-in; the code is good for one tablet, once, for five minutes.

const problemCopy = (error) => {
  if (problemOf(error) === OFFLINE) {
    return "You're offline. Setting up a tablet needs a connection.";
  }
  const status = error?.response?.status;
  if (status === 401) return "Your sign-in has ended. Sign in again to set up tablets.";
  if (status === 403) return "Your account can't set up learner tablets for this class.";
  return "Tablet setup isn't working right now. Please try again in a moment.";
};

// Refused because this app's sign-in is missing or has ended: only signing in
// again helps, so that is the action offered.
const needsSignIn = (error) =>
  problemOf(error) !== OFFLINE && error?.response?.status === 401;

const secondsLeft = (expiresAt, now) =>
  Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 1000));

const Countdown = ({ expiresAt, onExpired }) => {
  const [now, setNow] = useState(() => Date.now());
  const left = secondsLeft(expiresAt, now);
  useEffect(() => {
    if (left === 0) {
      onExpired();
      return undefined;
    }
    const timer = setTimeout(() => setNow(Date.now()), 1000);
    return () => clearTimeout(timer);
  }, [left, onExpired]);
  const minutes = Math.floor(left / 60);
  const seconds = String(left % 60).padStart(2, "0");
  return (
    <span aria-live="off">
      {minutes}:{seconds}
    </span>
  );
};

const Choice = ({ selected, onSelect, children }) => (
  <li>
    <button
      type="button"
      className={`${ui.card} ${ui.row}`}
      style={{
        width: "100%",
        minHeight: 56,
        textAlign: "left",
        border: selected ? "2px solid var(--primary-color, #43b061)" : undefined,
      }}
      aria-pressed={selected}
      onClick={onSelect}
    >
      <span className={ui.cardTitle} style={{ fontSize: "1rem" }}>
        {children}
      </span>
      {selected && <CheckIcon />}
    </button>
  </li>
);

const TabletSetup = () => {
  const { signOut } = useAuth();
  const [load, setLoad] = useState({ status: "loading" });
  const [classroomId, setClassroomId] = useState(null);
  const [learnerId, setLearnerId] = useState(null);
  const [code, setCode] = useState(null); // { learnerName, qrSvg, expiresAt, expired }
  const [making, setMaking] = useState(false);
  const [problem, setProblem] = useState(null);

  const fetchClassrooms = useCallback(async () => {
    setLoad({ status: "loading" });
    try {
      const classrooms = await readClassrooms();
      setLoad({ status: "ready", classrooms });
      if (classrooms.length === 1) setClassroomId(classrooms[0].id);
    } catch (error) {
      setLoad({ status: "error", message: problemCopy(error), signIn: needsSignIn(error) });
    }
  }, []);

  useEffect(() => {
    if (isTabletSetupAvailable()) fetchClassrooms();
  }, [fetchClassrooms]);

  const make = async () => {
    setMaking(true);
    setProblem(null);
    try {
      setCode({ ...(await createSetupCode({ classroomId, learnerId })), expired: false });
    } catch (error) {
      setProblem({ message: problemCopy(error), signIn: needsSignIn(error) });
    } finally {
      setMaking(false);
    }
  };

  const expire = useCallback(() => setCode((c) => c && { ...c, expired: true }), []);

  const back = (
    <Link to="/" className={ui.textButton}>
      <BackIcon />
      Today
    </Link>
  );

  const frame = (body, action) => (
    <>
      <div className={ui.page}>
        {back}
        <div className={ui.stack}>
          <p className={ui.eyebrow}>Learner tablets</p>
          <h1 className={ui.title}>Set up learner tablet</h1>
        </div>
        {body}
      </div>
      {action && (
        <div className={ui.actionBar}>
          <div className={ui.actionBarInner}>{action}</div>
        </div>
      )}
    </>
  );

  if (!isTabletSetupAvailable()) {
    return frame(
      <Message title="Tablet setup isn't available here">
        Setting up a learner tablet needs the school's live Thuto Time connection.
      </Message>
    );
  }

  if (load.status === "loading") {
    return frame(<LoadingCards label="Getting your classes…" count={1} />);
  }

  if (load.status === "error") {
    return frame(
      <Message
        title="We couldn't get your classes"
        actions={
          load.signIn ? (
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={signOut}>
              Sign in again
            </button>
          ) : (
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={fetchClassrooms}>
              Try again
            </button>
          )
        }
      >
        {load.message}
      </Message>
    );
  }

  // The code, once made.
  if (code) {
    const another = () => {
      setCode(null);
      setLearnerId(null);
    };
    return frame(
      code.expired ? (
        <Message
          title="This code has run out"
          actions={
            <button type="button" className={`${ui.button} ${ui.primary}`} onClick={make} disabled={making}>
              {making ? "Making a new code…" : "Make a new code"}
            </button>
          }
        >
          Codes last five minutes. Make a new one for {code.learnerName}'s tablet.
        </Message>
      ) : (
        <section className={`${ui.card} ${ui.stack}`} style={{ alignItems: "center", textAlign: "center" }}>
          <h2 className={ui.cardTitle}>Scan this with {code.learnerName}'s tablet</h2>
          <img
            src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(code.qrSvg)}`}
            alt={`Setup code for ${code.learnerName}'s tablet`}
            style={{ width: "min(100%, 280px)", height: "auto", background: "#fff", padding: 8, borderRadius: 10 }}
          />
          <p className={ui.meta}>
            <span>
              On the tablet, open Thuto Time and choose <strong>Scan setup code</strong>.
            </span>
          </p>
          <p className={ui.meta}>
            <span>
              Works once · runs out in <Countdown expiresAt={code.expiresAt} onExpired={expire} />
            </span>
          </p>
        </section>
      ),
      <>
        <button type="button" className={`${ui.button} ${ui.secondary}`} onClick={another}>
          Another tablet
        </button>
        <Link to="/" className={`${ui.button} ${ui.primary} ${ui.grow}`}>
          Done
        </Link>
      </>
    );
  }

  const { classrooms } = load;
  const classroom = classrooms.find((c) => c.id === classroomId);

  if (classrooms.length === 0) {
    return frame(
      <Message title="No classes to set up">
        None of your classes can have learner tablets yet.
      </Message>
    );
  }

  const body = (
    <>
      {classrooms.length > 1 && (
        <section className={ui.stack} aria-labelledby="choose-class">
          <h2 id="choose-class" className={ui.eyebrow}>
            Class
          </h2>
          <ul className={ui.list}>
            {classrooms.map((c) => (
              <Choice
                key={c.id}
                selected={c.id === classroomId}
                onSelect={() => {
                  setClassroomId(c.id);
                  setLearnerId(null);
                }}
              >
                {c.name}
              </Choice>
            ))}
          </ul>
        </section>
      )}

      {classroom && (
        <section className={ui.stack} aria-labelledby="choose-learner">
          <h2 id="choose-learner" className={ui.eyebrow}>
            {classrooms.length > 1 ? "Learner" : `Learner · ${classroom.name}`}
          </h2>
          {classroom.learners.length ? (
            <ul className={ui.list}>
              {classroom.learners.map((l) => (
                <Choice key={l.id} selected={l.id === learnerId} onSelect={() => setLearnerId(l.id)}>
                  {l.name}
                </Choice>
              ))}
            </ul>
          ) : (
            <p className={ui.meta}>No learners in this class can have a tablet yet.</p>
          )}
        </section>
      )}

      {problem && (
        <p className={ui.error} role="alert">
          {problem.message}
          {problem.signIn && (
            <>
              {" "}
              <button type="button" className={ui.textButton} onClick={signOut}>
                Sign in again
              </button>
            </>
          )}
        </p>
      )}
    </>
  );

  return frame(
    body,
    <button
      type="button"
      className={`${ui.button} ${ui.primary} ${ui.grow}`}
      onClick={make}
      disabled={!learnerId || making}
    >
      <DeviceIcon />
      {making ? "Making a code…" : "Set up learner tablet"}
    </button>
  );
};

export default TabletSetup;
